import { createFileRoute, Link } from "@tanstack/react-router";
import { pageSchemaScript, PERSON_ID } from "@/seo/schema";
import { PageShell } from "@/components/PageShell";
import { SectionHeader } from "@/components/SectionHeader";
import { Reveal } from "@/components/Reveal";
import { ArrowRight, Film, MapPin, ShieldCheck, Sparkles, Layers, Volume2 } from "lucide-react";
import solCinema from "@/assets/sol-cinema.jpg";

import { ogUrl } from "@/lib/ogSections";

const SITE_ORIGIN = "https://arifkhanglobal.com";
const URL = `${SITE_ORIGIN}/locations/home-cinema-consultant-delhi-ncr`;
const TITLE = "Home Cinema Consultant in Delhi NCR | Arif Khan";
const DESCRIPTION =
  "Independent home cinema consultant in Delhi NCR — reference 7.2.4 / 9.4.6 Atmos rooms, acoustic shell, isolation and calibration for private homes.";


const WHO_FOR = [
  "Owners building or renovating a private theatre in a Delhi, Gurugram, Noida, Faridabad or Ghaziabad residence",
  "Architects and interior designers coordinating an Atmos cinema with civil and MEP scope",
  "Developers planning a clubhouse or premium experience cinema in an NCR project",
  "Farmhouse owners around Delhi NCR wanting a dedicated screening room separate from the main residence",
];

const PLAN = [
  { Icon: Layers, title: "Acoustic shell & isolation", desc: "Floating floor, decoupled walls, isolated ceiling and HVAC routed around the room — not through it." },
  { Icon: Volume2, title: "Speaker & seat geometry", desc: "7.2.4 or 9.4.6 Atmos layouts mapped against seat rows and reference axis before any speaker is procured." },
  { Icon: Film, title: "Projection & screen", desc: "Projector throw, screen size, gain and ambient-light handling chosen for the actual room — not a brochure scene." },
  { Icon: Sparkles, title: "Calibration & control", desc: "Independent calibration, scene presets and a single touch UX for owners — not a 12-remote experience." },
];

const MISTAKES = [
  "Cinema room finalised after tiling, false ceiling and HVAC are frozen",
  "Speaker brand chosen before acoustic shell is engineered",
  "HVAC ducts running directly through the cinema ceiling",
  "Seating row placed without locking the reference axis to the screen",
  "No isolation from adjacent bedrooms — sound leaks both ways",
];

const FAQS = [
  { q: "What does a private home cinema cost in Delhi NCR?", a: "A genuine, acoustically-engineered Atmos cinema for a Delhi NCR residence typically starts around ₹35–60 lakh for a 7.2.4 room and ₹80 lakh to ₹2 crore+ for a flagship 9.4.6 space, depending on room shape, isolation and finishes. The civil and acoustic prep is the largest swing factor." },
  { q: "When should we involve a home cinema consultant?", a: "At architectural design — before column grids, ceiling heights, HVAC routing and door positions are frozen. Bringing a consultant in after civil work is the single most common cause of compromised cinemas in NCR." },
  { q: "Do you work directly with our architect and interior designer?", a: "Yes. We coordinate room geometry, isolation, HVAC routing, finishes and cabling pathways with your architectural and interior teams so the cinema integrates cleanly into the residence." },
  { q: "Can you upgrade an existing media room into a reference cinema?", a: "Often, yes — with the right acoustic treatment, isolation work, re-positioned seating and a calibrated speaker layout. We audit the room first and tell you honestly what the ceiling of the existing shell is." },
];

const RELATED = [
  { to: "/solutions/luxury-home-cinema", label: "Luxury Home Cinema solution" },
  { to: "/industries/luxury-residences", label: "Luxury Residences & Villas" },
  { to: "/industries/farmhouses-private-estates", label: "Farmhouses & Private Estates" },
  { to: "/ask-arif/home-cinema-acoustic-design-basics", label: "Home cinema acoustic design basics" },
  { to: "/ask-arif/common-mistakes-in-home-theatre-projects", label: "Mistakes in home theatre projects" },
  { to: "/for-architects", label: "For Architects — design-stage coordination" },
] as const;

export const Route = createFileRoute("/locations/home-cinema-consultant-delhi-ncr")({
  component: Page,
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: URL },
      { property: "og:type", content: "article" },
      { property: "og:image", content: ogUrl(solCinema) },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESCRIPTION },
      { name: "twitter:image", content: ogUrl(solCinema) },
      { name: "geo.region", content: "IN-DL" },
      { name: "geo.placename", content: "Delhi NCR" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      pageSchemaScript(
        {
          path: "/locations/home-cinema-consultant-delhi-ncr",
          type: "WebPage",
          name: TITLE,
          description: DESCRIPTION,
          breadcrumb: [{ name: "Locations", url: "/locations" }, { name: "Home Cinema Consultant in Delhi NCR" }],
          extra: { about: { "@id": `${URL}#service` }, mainEntity: { "@id": `${URL}#service` } },
        },
        [
          {
            "@type": "Service",
            "@id": `${URL}#service`,
            name: "Home Cinema Consultant — Delhi NCR",
            serviceType: "Home Cinema Design & Acoustic Engineering Consultancy",
            provider: { "@id": PERSON_ID },
            areaServed: [{ "@type": "City", name: "New Delhi" }, { "@type": "City", name: "Gurugram" }, { "@type": "City", name: "Noida" }, { "@type": "City", name: "Faridabad" }, { "@type": "City", name: "Ghaziabad" }],
            url: URL,
            description: DESCRIPTION,
          },
          {
            "@type": "FAQPage",
            "@id": `${URL}#faq`,
            url: URL,
            isPartOf: { "@id": `${URL}#webpage` },
            mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
          },
        ],
      ),
    ],
  }),
});

function Page() {
  return (
    <PageShell>
      <section className="relative pt-40 pb-24 border-b border-border bg-surface overflow-hidden">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div aria-hidden className="absolute inset-0 opacity-50" style={{ backgroundImage: `url(${solCinema})`, backgroundSize: "cover", backgroundPosition: "center", filter: "blur(40px) saturate(1.1)", transform: "scale(1.15)" }} />
        <div className="absolute inset-0 bg-gradient-to-t from-surface via-surface/85 to-surface/60" />
        <div className="container-page relative">
          <div className="flex items-center gap-2 text-ink-soft">
            <MapPin className="h-4 w-4" />
            <span className="eyebrow">Delhi NCR · Locations</span>
          </div>
          <h1 className="mt-6 text-balance text-5xl md:text-7xl font-semibold tracking-tight leading-[0.95] max-w-4xl">
            Home Cinema Consultant in Delhi&nbsp;NCR
          </h1>
          <p className="mt-6 max-w-2xl text-xl text-ink-soft leading-relaxed">
            Independent, acoustic-first private cinema design for residences and farmhouses across Delhi, Gurugram, Noida, Faridabad and Ghaziabad. Reference-grade 7.2.4 and 9.4.6 Dolby Atmos rooms — engineered before any speaker is chosen.
          </p>
        </div>
      </section>

      <section className="py-24">
        <div className="container-page grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5"><SectionHeader eyebrow="Who this is for" title="Owners and design teams in Delhi NCR." /></div>
          <div className="lg:col-span-7 grid gap-4">
            {WHO_FOR.map((w, i) => (
              <Reveal key={w} delay={i * 0.04}>
                <div className="flex items-start gap-4 rounded-xl border border-border bg-surface p-5">
                  <div className="font-mono text-xs text-ink-soft w-8 pt-0.5">0{i + 1}</div>
                  <div className="text-sm text-foreground leading-relaxed">{w}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-surface border-y border-border">
        <div className="container-page">
          <SectionHeader eyebrow="What we plan" title="A cinema engineered before the room is built." description="The decisions that decide a reference-grade outcome — sequenced before civil work locks them in." />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {PLAN.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.05}>
                <div className="h-full rounded-2xl border border-border bg-background p-6">
                  <p.Icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  <h3 className="mt-6 text-lg font-semibold tracking-tight">{p.title}</h3>
                  <p className="mt-3 text-sm text-ink-soft leading-relaxed">{p.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container-page grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5"><SectionHeader eyebrow="Common mistakes" title="What we see go wrong in NCR cinemas." description="Patterns that show up when the cinema is treated as a finishing item instead of an architectural one." /></div>
          <div className="lg:col-span-7 grid gap-4">
            {MISTAKES.map((m, i) => (
              <Reveal key={m} delay={i * 0.04}>
                <div className="flex items-start gap-4 rounded-xl border border-border bg-surface p-5">
                  <div className="font-mono text-xs text-ink-soft w-8 pt-0.5">{String(i + 1).padStart(2, "0")}</div>
                  <div className="text-sm text-foreground leading-relaxed">{m}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-surface border-y border-border">
        <div className="container-page grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5"><SectionHeader eyebrow="Related services" title="Where this connects." /></div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {RELATED.map((r) => (
              <Link key={r.to} to={r.to as "/"} className="group flex items-center justify-between rounded-xl border border-border bg-background p-5 hover:border-border-strong hover:shadow-card transition-all">
                <span className="text-sm font-medium">{r.label}</span>
                <ArrowRight className="h-4 w-4 text-ink-soft group-hover:text-primary transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="container-page grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5"><SectionHeader eyebrow="FAQ" title="Questions we hear from NCR owners." /></div>
          <div className="lg:col-span-7 grid gap-4">
            {FAQS.map((f, i) => (
              <Reveal key={f.q} delay={i * 0.05}>
                <details className="group rounded-xl border border-border bg-background p-5 open:border-border-strong open:shadow-card transition-all">
                  <summary className="flex cursor-pointer list-none items-start justify-between gap-4 font-medium">
                    <span>{f.q}</span>
                    <ShieldCheck className="h-4 w-4 mt-1 text-ink-soft group-open:text-primary transition-colors" />
                  </summary>
                  <p className="mt-3 text-sm text-ink-soft leading-relaxed">{f.a}</p>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 border-t border-border bg-surface">
        <div className="container-page flex flex-col md:flex-row md:items-center md:justify-between gap-8">
          <div>
            <h2 className="text-3xl md:text-4xl font-semibold tracking-tight leading-tight max-w-2xl text-balance">Planning a private cinema in Delhi NCR?</h2>
            <p className="mt-3 text-ink-soft">Share your floor plan, room dimensions and project stage. An honest, acoustic-first review before civil work begins.</p>
          </div>
          <div className="flex flex-wrap gap-3">
            <a href={`mailto:arif@arifkhanglobal.com?subject=${encodeURIComponent("Home Cinema Consultant — Delhi NCR")}`} className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-medium text-primary-foreground hover:shadow-elevated transition-shadow">
              Email Your Requirement <ArrowRight className="h-4 w-4" />
            </a>
            <Link to="/contact" className="inline-flex items-center gap-2 rounded-full border border-border-strong bg-background px-7 py-3.5 text-sm font-medium hover:border-primary hover:text-primary transition-colors">
              Let's Talk
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
